import { answerIndex } from "@/data/questionnaire";

// DOCTHAIR HAIR HEALTH SCORE — lecture déclarative.
//
// Ce score est calculé UNIQUEMENT à partir des réponses de l'utilisateur.
// Il ne mesure rien sur la fibre, ne remplace aucun examen et n'est pas un
// diagnostic. Chaque option porte un poids de charge explicite entre 0 et 1 :
// la méthode est lisible, vérifiable et affichée à l'utilisateur.
//
// Les poids sont indexés sur la POSITION de l'option, jamais sur son libellé,
// afin de rester valables en français comme en anglais.
//
// Toutes les échelles ne sont pas croissantes : « Je ne sais pas » sur
// l'évolution d'une chute ne vaut pas « en aggravation ». D'où une table
// explicite plutôt qu'un simple indice divisé par le nombre d'options.
const LOAD = {
  dryness: [0, 0.33, 0.66, 1],
  breakage: [0, 0.33, 0.66, 1],
  chem_reaction: [0, 0.33, 0.66, 1],
  straightening: [0, 0.25, 0.6, 1],
  bleaching: [0, 0.35, 0.7, 1],
  colouring: [0, 0.3, 0.65, 1],
  heat: [0, 0.3, 0.7, 1],
  itching: [0, 0.33, 0.66, 1],
  scalp_signs: [0, 0.5, 0.7, 0.9],
  scalp_pain: [0, 0.4, 0.8, 1],
  shedding: [0, 0.33, 0.66, 1],
  shedding_trend: [0, 0.35, 1, 0.5],
  patches: [0, 0.7, 0.9, 1],
  styling: [0, 0.25, 0.6, 0.9],
  styling_tension: [0, 0.3, 0.65, 1],
};

// Chaque dimension agrège quelques signaux pondérés.
// Une question non posée — parce qu'une branche est fermée — est simplement
// ignorée, et son poids est réparti sur les signaux réellement disponibles.
export const DIMENSIONS = [
  { id: "fibre", signals: { dryness: 0.5, chem_reaction: 0.5 } },
  { id: "fragility", signals: { breakage: 0.5, bleaching: 0.25, straightening: 0.25 } },
  { id: "scalp", signals: { itching: 0.4, scalp_signs: 0.3, scalp_pain: 0.3 } },
  { id: "density", signals: { shedding: 0.45, shedding_trend: 0.3, patches: 0.25 } },
  { id: "routine", signals: { heat: 0.35, styling: 0.25, colouring: 0.2, styling_tension: 0.2 } },
];

// Zone de référence affichée derrière chaque barre.
// ATTENTION : il s'agit d'une grille de lecture interne, PAS d'une cohorte
// clinique ni d'une norme médicale. Elle situe une valeur « habituelle » pour
// une personne sans signal particulier sur la dimension concernée.
export const REFERENCE_BAND = { from: 62, to: 88 };

const scoreFor = (questions, answers, signals) => {
  let weight = 0;
  let load = 0;
  Object.entries(signals).forEach(([id, signalWeight]) => {
    const index = answerIndex(questions, answers, id);
    const table = LOAD[id];
    if (index < 0 || !table || table[index] === undefined) return;
    weight += signalWeight;
    load += signalWeight * table[index];
  });
  if (weight === 0) return null;
  return Math.round(100 - (load / weight) * 100);
};

export const band = (value) => {
  if (value === null) return "unknown";
  if (value >= 75) return "solid";
  if (value >= 50) return "watch";
  return "fragile";
};

export const computeScore = (questions, answers) => {
  const dimensions = DIMENSIONS.map((dimension) => {
    const value = scoreFor(questions, answers, dimension.signals);
    return { id: dimension.id, value, band: band(value) };
  });

  const scored = dimensions.filter((dimension) => dimension.value !== null);
  const global = scored.length
    ? Math.round(scored.reduce((sum, dimension) => sum + dimension.value, 0) / scored.length)
    : null;

  return { global, band: band(global), dimensions };
};

export const SCORE_COPY = {
  fr: {
    overline: "DOCTHAIR Hair Health Score",
    title: "Votre indice capillaire déclaratif.",
    lead: "Cet indice résume vos réponses en cinq dimensions. Il donne un point de départ mesurable, à confronter ensuite à une analyse de la fibre.",
    globalLabel: "Indice global",
    outOf: "sur 100",
    dimensions: {
      fibre: { name: "Fibre", body: "Sécheresse et tolérance aux services chimiques." },
      fragility: { name: "Fragilité", body: "Casse déclarée, décoloration et lissages." },
      scalp: { name: "Cuir chevelu", body: "Démangeaisons, signes visibles et inconfort." },
      density: { name: "Densité", body: "Chute perçue, évolution récente et zones dégarnies." },
      routine: { name: "Routine", body: "Chaleur, coiffures sous tension et coloration." },
    },
    bands: { solid: "Solide", watch: "À surveiller", fragile: "Fragile", unknown: "Non évalué" },
    progression: "Progression",
    progressionBody: "Se débloque à partir de votre deuxième lecture, pour comparer votre évolution.",
    progressionTag: "À venir",
    referenceLabel: "Zone de référence",
    savedRemote: "Cet indice est enregistré dans votre espace. Vous pourrez le comparer à vos prochaines lectures et suivre votre évolution.",
    savedLocal: "Cet indice est conservé sur cet appareil pour que vous puissiez le comparer à vos prochaines lectures. La synchronisation avec votre espace sera activée prochainement.",
    kitCta: "Aller plus loin avec le kit",
    accountCta: "Voir mon espace",
    methodTitle: "Comment cet indice est calculé",
    methodBody: "Chaque réponse porte un poids de charge compris entre 0 et 1, agrégé par dimension. Une question non posée est ignorée et son poids réparti sur les autres. La zone de référence est une grille de lecture interne : elle ne provient pas d'une cohorte clinique et ne constitue pas une norme médicale.",
    disclaimer: "Indice déclaratif, à visée informative. Il repose uniquement sur vos réponses, ne mesure pas la fibre et ne constitue en aucun cas un diagnostic médical.",
  },
  en: {
    overline: "DOCTHAIR Hair Health Score",
    title: "Your declarative hair index.",
    lead: "This index summarises your answers across five dimensions. It gives a measurable starting point, to be compared later with a fibre analysis.",
    globalLabel: "Overall index",
    outOf: "out of 100",
    dimensions: {
      fibre: { name: "Fibre", body: "Dryness and tolerance to chemical services." },
      fragility: { name: "Fragility", body: "Declared breakage, bleaching and smoothing." },
      scalp: { name: "Scalp", body: "Itching, visible signs and discomfort." },
      density: { name: "Density", body: "Perceived shedding, recent trend and patches." },
      routine: { name: "Routine", body: "Heat, tension hairstyles and colouring." },
    },
    bands: { solid: "Solid", watch: "Worth watching", fragile: "Fragile", unknown: "Not assessed" },
    progression: "Progression",
    progressionBody: "Unlocks from your second reading, to compare your evolution.",
    progressionTag: "Coming",
    referenceLabel: "Reference range",
    savedRemote: "This index is saved to your account. You will be able to compare it with your next readings and follow your evolution.",
    savedLocal: "This index is kept on this device so you can compare it with your next readings. Syncing with your account will be enabled shortly.",
    kitCta: "Go further with the kit",
    accountCta: "Go to my account",
    methodTitle: "How this index is calculated",
    methodBody: "Each answer carries a load between 0 and 1, aggregated per dimension. A question that was not asked is ignored and its weight redistributed. The reference range is an internal reading grid: it does not come from a clinical cohort and is not a medical norm.",
    disclaimer: "Declarative index, for information only. It relies solely on your answers, does not measure the fibre, and is in no way a medical diagnosis.",
  },
};
